import { ErrorWithBreadcrumb } from '~/errors';

export const useUserConfigurationStore = defineStore('userConfiguration', () => {
  const configurationAPI = useConfigurationAPI();
  const fitterAPI = useFitterAPI();

  const userConfiguration = ref<UserConfiguration>();
  const _fitters = ref<FitterResponseDTO[] | null>(null);

  const config = computed(() => {
    const _userConfiguration = userConfiguration.value;

    if (_userConfiguration === undefined) {
      throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.UserConfiguration, message: 'User configuration is not set' });
    }

    // I have chosen to use a type assertion here instead of changing the UserConfiguration interface as the roles are lowercase strings while the roles in the interface isn't
    const lowercaseRoles = _userConfiguration.Roles.map(role => role.toLowerCase()) as Roles[];

    // if the user is a lightuser (HourRegistration) remove all other roles as they get overwritten by lightuser
    const filteredRoles = lowercaseRoles.includes(Roles.HourRegistration) ? [Roles.HourRegistration] : lowercaseRoles;

    const newConfig = { ..._userConfiguration, Roles: filteredRoles };

    return readonly(newConfig);
  });

  const roles = computed(() => config.value.Roles);
  const fitterId = computed(() => config.value.FitterID);
  const isProjectManager = computed(() => roles.value.includes(Roles.ProjectManager));
  const isLightUser = computed(() => roles.value.includes(Roles.HourRegistration));
  const isTeamLead = computed(() => roles.value.includes(Roles.TeamLeader));
  const isProjectResponsible = computed(() => roles.value.includes(Roles.ProjectResponsible));
  const canCreateProject = computed(() => roles.value.includes(Roles.FitterProject));
  const canCreateDebtor = computed(() => roles.value.includes(Roles.FitterDebtor));

  const initials = computed(() => {
    const name = config.value.FitterName;

    if (name === undefined) {
      return '';
    }

    return name
      .split(' ')
      .map((subname, index, array) => index === 0 || index === array.length - 1 ? subname.charAt(0) : '')
      .join('')
      .toUpperCase();
  });

  async function setUserConfiguration() {
    const response = await configurationAPI.getUserConfiguration();

    if (response && !response.HasErrors) {
      userConfiguration.value = response.Data;
    }
  }

  async function fetchFitters() {
    const response = await fitterAPI.getFitters();

    if (response && !response.HasErrors) {
      _fitters.value = response.Data;
    }
  }

  const canSeeItemPricesInApp = computed(() => {
    const canSeeItemPricesInAppClaim = config.value.UserClaims.find(claim => claim.ClaimType === 'CanSeeItemPricesInApp');

    return canSeeItemPricesInAppClaim?.ClaimValue === 'True';
  });

  const canDeleteCalendarEventsInApp = computed(() => {
    const canDeleteAppointmentsInApp = config.value.UserClaims.find(e => e.ClaimType === 'CanDeleteAppointmentsInApp');

    return canDeleteAppointmentsInApp?.ClaimValue === 'True';
  });

  const fitters = computed(() => {
    if (_fitters.value === null) {
      throw new Error('Fitters for team lead is not set');
    }

    return readonly(_fitters.value);
  });

  const teamMembers = computed(() => readonly(fitters.value.filter(fitter => fitter.IsTeamMember)));

  const usersFitter = computed(() => {
    const fitter = fitters.value.find(fitter => fitter.FitterID === fitterId.value);

    if (fitter === undefined) {
      throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.UserConfiguration, message: 'User not found in fitters list' });
    }

    return fitter;
  });

  const teamMembersAndUser = computed(() => {
    if (teamMembers.value.find(fitter => fitter.FitterID === fitterId.value) === undefined) {
      return teamMembers.value.concat(usersFitter.value);
    } else {
      return teamMembers.value;
    }
  });

  const trimmedSiteName = computed(() => {
    const raw = config.value.SiteName ?? '';
    const withoutPrefix = raw.replace(/^ekomplet_/i, '');

    return withoutPrefix.charAt(0).toUpperCase() + withoutPrefix.slice(1);
  });

  function $reset(): void {
    userConfiguration.value = {
      Roles: [],
      UserClaims: [],
      FitterID: 0,
      FitterName: '',
      SiteName: '',
      UserName: '',
      IsWebUser: false,
      HasBackgroundCheckCertificate: false,
      Locale: ''
    };
  }

  return {
    _userConfiguration: userConfiguration,
    userConfiguration: readonly(userConfiguration),
    config,
    setUserConfiguration,
    initials,
    roles,
    canSeeItemPricesInApp,
    canDeleteCalendarEventsInApp,
    isTeamLead,
    isProjectManager,
    isLightUser,
    isProjectResponsible,
    canCreateProject,
    canCreateDebtor,
    fitterId,
    _fitters,
    fitters,
    teamMembers,
    trimmedSiteName,
    teamMembersAndUser,
    usersFitter,
    fetchFitters,
    $reset
  };
});
